// packages/core/src/learning/school-modules/growth-investing.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for the 'growth-investing' school. LearningModule, surface 'school'.
//   module id   = 'school-growth-investing'  (matches exhibit nativeModuleId)
//   homeSchool  = 'growth-investing'
// PROMOTED: five authored lessons (above the divider in the index).
//
// Placeholder presentational fields (emoji, accentColor, tier, order) carried
// from the stub conventions — tune per-school in the presentation pass.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_GROWTH_INVESTING_MODULE: LearningModule = {
	id: 'school-growth-investing',
	title: 'Growth Investing',
	description:
		'The lens that bets on future earnings power — reading the quality of growth, rejecting lazy cheapness, and respecting valuation risk and hype.',
	emoji: '🚀',
	accentColor: '#2d7dd2',
	tier: 'growth',
	pillar: 'long-term-investing',
	order: 0,
	surface: 'school',
	homeSchool: 'growth-investing',
	lessons: [
		{
			id: 'school-growth-investing-01',
			title: 'Growth is a bet on the future',
			description:
				'This lesson teaches how growth investors see future earnings power as the center of the story.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['growth-investing'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['growth-investing', 'future-earnings', 'expectation-gap'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'future earnings power',
				'reinvestment runway',
				'expectation gap',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Growth investing starts with a simple problem: the market can look backward while a business is changing forward. A company may look expensive today because the important part has not fully shown up yet.',
				},
				{
					type: 'paragraph',
					text: 'This school is paying attention to businesses that can grow revenue, expand profits, and reinvest into more growth for a long time. It does not only ask, “What is this worth today?” It asks, “What could this become if the business keeps improving?”',
				},
				{
					type: 'bullet_list',
					items: [
						'Future profits matter',
						'Reinvestment can compound',
						'The price already assumes something',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is thinking every fast growing company is a good investment.',
				},
				{
					type: 'example',
					text: 'Imagine two companies. One earns steady profits but has little room to grow. Another earns less today but keeps opening new markets, improving margins, and gaining customers. Growth investing studies the second company because today’s numbers may not show the full story.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that the future is uncertain. A business can grow slower than expected, competition can catch up, or the price can already assume a perfect outcome.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to scenario thinking. You are not being told what to buy. You are learning to separate a real growth thesis from excitement, hype, and price blindness.',
				},
				{
					type: 'key_takeaway',
					text: 'Growth investing is not about liking the future. It is about testing what future the price already assumes.',
				},
			],
			audioScript:
				'Growth investing begins with a different way of looking at value. A lot of investors look at a company and ask, “What does it earn right now?” Growth investors ask that too, but they do not stop there. They ask, “What could this business earn later if the growth keeps going, if the market expands, if the company keeps reinvesting well, and if customers keep choosing it?”\n\nThat is the heart of the lens. Growth investing is not just buying companies with exciting names. It is a belief that some businesses are in the middle of becoming much larger than they currently look. The current numbers might be incomplete. The income statement may show today, but the real story might be five or ten years ahead.\n\nThink about a small business that keeps putting money back into better products, better distribution, better software, better customer service, or a bigger market. It may not maximize profit today because it is choosing to grow. To a traditional investor, that can look unattractive. To a growth investor, that can look like a machine being built.\n\nBut here is where people get sloppy. A company growing fast is not automatically a good investment. A company you admire is not automatically a good investment. A product you use every day is not automatically a good investment. Growth investing still has to deal with price. If the market already expects massive growth, the stock may need the future to go almost perfectly just to justify the current price.\n\nThat is why expectations matter so much. A growth investor is really asking two questions at the same time. First, how big can this business become? Second, how much of that future is already priced in?\n\nIf you only ask the first question, you can fall in love with a story. You see the customers, the headlines, the product, the excitement, and you start acting like growth removes risk. It does not. Growth companies can disappoint. Competition can arrive. Margins can shrink. Interest rates can change how investors value future profits. A business can keep growing and still be a poor investment if the price was too high.\n\nInside Nisk, treat this as a thinking lens. You are not trying to guess magic winners. You are learning how a worldview interprets money. Growth investing says the future can matter more than the present, but it also demands humility. The future has to become real, not just interesting.\n\nA clean growth thesis sounds like this: “Here is what the business could become. Here is why that might happen. Here is what could break the thesis. Here is what the current price already seems to assume.” That is very different from saying, “This company is cool, so it must go up.”\n\nThe takeaway is simple. Growth investing is a bet on future earnings power. The danger is paying for a future that already has to be perfect.',
			flashcards: [
				{
					id: 'school-growth-investing-01-fc-1',
					role: 'main_idea',
					front: 'What does growth investing notice?',
					back: 'It notices future earnings power that may not show up clearly today.',
				},
				{
					id: 'school-growth-investing-01-fc-2',
					role: 'watch_out',
					front: 'What is the growth trap?',
					back: 'Confusing an exciting company with a sensible price.',
				},
				{
					id: 'school-growth-investing-01-fc-3',
					role: 'use_it',
					front: 'How do you test a growth story?',
					back: 'Ask what future must happen for the current price to make sense.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Simulate a growth case',
			},
			revision: 1,
		},
		{
			id: 'school-growth-investing-02',
			title: 'The mistake is worshiping cheap prices',
			description:
				'This lesson teaches why growth investing pushes back against judging opportunity by cheapness alone.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['growth-investing'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['growth-investing', 'cheapness-trap', 'market-size'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['cheapness trap', 'quality growth', 'market size'],
			blocks: [
				{
					type: 'paragraph',
					text: 'A low price can feel safe. Growth investing warns that cheap can also mean stuck, shrinking, or low quality.',
				},
				{
					type: 'paragraph',
					text: 'This school believes some businesses deserve attention even when they look expensive by simple measures. The reason is not blind optimism. The reason is that a strong business with room to grow may be building future value faster than today’s price ratios suggest.',
				},
				{
					type: 'bullet_list',
					items: [
						'Cheap can be a warning',
						'Expensive can be incomplete',
						'Growth needs proof',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating a low price like protection without asking why the market is skeptical.',
				},
				{
					type: 'example',
					text: 'A company trading at a low valuation may be losing customers, stuck in a weak industry, or unable to reinvest profitably. Another company may look pricey because it is taking share, building a strong brand, and expanding into a larger market.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is that growth investors can swing too far the other way. They can excuse any valuation by saying the future is big.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens is useful when comparing stories. The question is not “cheap or expensive?” The question is “what does the price assume, and what would need to go right?”',
				},
				{
					type: 'key_takeaway',
					text: 'Growth investing does not reject price. It rejects lazy cheapness.',
				},
			],
			audioScript:
				'One of the biggest differences between growth investing and some other investing lenses is how it treats cheapness. A cheap looking price can feel comforting. It gives the sense that you found a bargain. Growth investing does not ignore that, but it pushes back on the idea that cheap automatically means good.\n\nSometimes cheap is cheap for a reason. A business may be losing relevance. Its market may be shrinking. Its product may be replaceable. Its management may be weak. Its customers may be leaving. Its profits may look stable today, while the real future is slowly getting worse.\n\nGrowth investing is paying attention to a different kind of mistake. It says, “Do not focus so much on today’s low price that you miss tomorrow’s expanding business.” A company with strong growth, a large market, loyal customers, and the ability to reinvest may look expensive through simple measures. But if the future cash flows grow enough, today’s expensive looking price may not tell the whole story.\n\nThat does not mean expensive is good. It means simple cheapness is not enough.\n\nImagine two businesses. Business A trades at a low price compared with current earnings. It looks like a bargain. But sales are flat, younger customers do not care about the brand, and the company has no clear place to reinvest. Business B trades at a much higher valuation. It looks expensive. But it is gaining customers, expanding into new categories, improving margins, and still has a large market ahead. A growth investor does not automatically choose Business B, but they understand why Business B might deserve deeper study.\n\nThe protection this school offers is against small thinking. It protects against only valuing what already exists. It says, “The future matters. Market size matters. Reinvestment matters. Business momentum matters.”\n\nBut the blind spot is real. Growth investors can become too forgiving. They can say, “This business will be huge,” without asking whether the current price already assumes huge. They can ignore losses, competition, weak economics, or unrealistic expectations because the story feels powerful.\n\nThis is why a mature growth lens still respects valuation. It does not worship cheap prices, but it also does not worship exciting stories. It asks a harder question: what has to happen for this price to make sense?\n\nInside Nisk, use this as a thinking exercise. When you hear a growth story, split it into pieces. What is the market? Why might this company win? How could it make money at scale? What could break? What does the current price already assume? That is the difference between studying growth and chasing hype.\n\nThe takeaway is direct. Growth investing is not anti price. It is anti lazy cheapness. It believes a business can be worth more than today’s numbers suggest, but the future still has to earn the valuation.',
			flashcards: [
				{
					id: 'school-growth-investing-02-fc-1',
					role: 'main_idea',
					front: 'What cheapness mistake does growth fight?',
					back: 'It fights judging opportunity only by low current valuation.',
				},
				{
					id: 'school-growth-investing-02-fc-2',
					role: 'watch_out',
					front: 'When can cheap be dangerous?',
					back: 'When the business is shrinking, weak, or unable to reinvest well.',
				},
				{
					id: 'school-growth-investing-02-fc-3',
					role: 'use_it',
					front: 'What question replaces cheap or expensive?',
					back: 'Ask what the price assumes and what must go right.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Compare price and story',
				prompt: 'Write one example where something looked cheap but had a weak future, then one where something looked expensive because the future was still developing.',
			},
			revision: 1,
		},
		{
			id: 'school-growth-investing-03',
			title: 'The tradeoff is valuation risk',
			description:
				'This lesson teaches the biggest growth investing tradeoff: the future may be real, but the price may already demand too much.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['growth-investing'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: ['growth-investing', 'valuation-risk', 'perfection-pricing'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'valuation risk',
				'perfection pricing',
				'thesis failure',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Growth investing can feel powerful because the upside story is easy to imagine. The risk is that the market may already be imagining it too.',
				},
				{
					type: 'paragraph',
					text: 'This school accepts a hard tradeoff. It may pay more today for a business that could be much larger tomorrow. That means the investment depends on the future showing up.',
				},
				{
					type: 'bullet_list',
					items: [
						'Expectations matter',
						'Growth can disappoint',
						'A good company can be overpriced',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is thinking a strong business and a strong investment are always the same thing.',
				},
				{
					type: 'example',
					text: 'A company can grow revenue, gain customers, and still fall short if investors expected even faster growth. The business may improve while the investment result disappoints.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is perfection pricing. When a price assumes huge growth, high margins, and few mistakes, even a small disappointment can hurt.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to runway and risk tolerance. Before chasing upside, you need to understand how much uncertainty your current money life can absorb.',
				},
				{
					type: 'key_takeaway',
					text: 'In growth investing, the danger is not only being wrong. It is being right but paying too much.',
				},
			],
			audioScript:
				'The hardest part of growth investing is not understanding why a business is exciting. That part is usually easy. The hard part is understanding what the price already assumes.\n\nThis is where valuation risk comes in. Growth investors accept that some businesses deserve higher prices because they may grow into them. A company with a huge market, strong customer demand, and high reinvestment potential may not look cheap today. The growth investor says, “That might be fine if the future becomes large enough.”\n\nBut there is a catch. The more the market already believes the story, the less room there is for disappointment. If the price already assumes rapid growth, strong margins, smooth execution, and years of dominance, then the company has to deliver a lot just to meet expectations.\n\nThis creates a strange situation. You can be right about the business and still be disappointed as an investor. The company can grow. Customers can increase. Revenue can rise. The product can get better. But if the market expected even more, the investment can still perform poorly.\n\nThat is one of the most important lessons in this school. A good company and a good investment are not the same thing. A great business at a price that assumes perfection can carry real risk. A good story can become dangerous when everyone already agrees with it.\n\nImagine a company growing revenue at a fast pace. People love the product. It is all over social media. Analysts talk about its future market. Investors call it the next big thing. The story may be real. But if the price already assumes years of nearly perfect execution, then any slowdown can change the entire picture. Growth does not need to stop for investors to get hurt. It only needs to disappoint the expectations baked into the price.\n\nThis is why growth investing requires emotional control. The excitement pulls you toward the upside. The discipline forces you to ask, “What if growth slows? What if margins stay lower than expected? What if competition arrives? What if the market is smaller than the story suggests?”\n\nInside Nisk, this is not about telling you what to buy or avoid. It is about learning how this worldview handles uncertainty. If your own money life has thin runway, high stress, or little margin for mistakes, valuation risk matters differently. A risky growth thesis might feel exciting on a screen but heavy in real life.\n\nThe growth lens is useful because it reminds you that the future can be valuable. But the mature version of the lens also reminds you that price is a claim on that future. If the claim is too demanding, the investment can break even when the company keeps improving.\n\nThe takeaway is simple. Growth investing is not just about finding the future. It is about not overpaying for a future everyone already sees.',
			flashcards: [
				{
					id: 'school-growth-investing-03-fc-1',
					role: 'main_idea',
					front: 'What is valuation risk?',
					back: 'The risk that the price already assumes too much future success.',
				},
				{
					id: 'school-growth-investing-03-fc-2',
					role: 'watch_out',
					front: 'Can a good company disappoint?',
					back: 'Yes. The business can improve while the investment falls short of expectations.',
				},
				{
					id: 'school-growth-investing-03-fc-3',
					role: 'use_it',
					front: 'What should you ask before upside?',
					back: 'Ask what disappointment would do if growth slows or margins miss.',
				},
			],
			applyAction: {
				type: 'calculateRunway',
				label: 'Check your runway first',
			},
			revision: 1,
		},
		{
			id: 'school-growth-investing-04',
			title: 'Growth changes how you read a business',
			description:
				'This lesson teaches how growth investors interpret business signals beyond today’s profit.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['growth-investing'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['growth-investing', 'business-signals', 'durable-advantage'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'business signals',
				'reinvestment quality',
				'durable advantage',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Growth investing reads a business like a story that is still being written. The point is not to ignore numbers, but to ask which numbers reveal the future.',
				},
				{
					type: 'paragraph',
					text: 'This school looks for signs that growth can keep going. It studies market size, customer demand, reinvestment quality, margins, retention, brand strength, and competitive advantage.',
				},
				{
					type: 'bullet_list',
					items: [
						'Is the market expanding?',
						'Can the company reinvest well?',
						'Do customers keep coming back?',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating revenue growth alone as proof of quality.',
				},
				{
					type: 'example',
					text: 'A company can grow quickly by spending heavily to acquire customers. That is not the same as building a durable business if those customers leave, margins never improve, or competitors copy the product.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is attention. This lens asks more from the user than simple indexing or a passive plan. You have to keep checking whether the original thesis is still alive.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this connects to writing your assumptions. A growth thesis should be clear enough that future you can tell whether the story improved or broke.',
				},
				{
					type: 'key_takeaway',
					text: 'Growth investing studies the quality of growth, not just the speed of growth.',
				},
			],
			audioScript:
				'Growth investing changes how you read a business. It does not only ask, “How much profit does this company make right now?” It asks, “What signals tell us whether this business can become much larger and stronger over time?”\n\nThat means the growth investor is looking for clues. Is the market expanding? Are more people adopting the product? Does the company have a reason customers stay? Can it raise prices without losing everyone? Can it reinvest money into better products, new markets, or stronger operations? Are margins likely to improve as the business scales? Is the company building something competitors will struggle to copy?\n\nThis is where growth investing becomes more serious than just chasing revenue. Revenue growth alone is not enough. A company can grow quickly by spending a lot of money to attract customers. That may look impressive for a while, but if the customers leave, if the business never becomes profitable, or if the product is easy to copy, the growth may not be high quality.\n\nGrowth investors care about the quality of growth. Good growth has a reason behind it. It may come from a better product, a strong brand, a network effect, a cost advantage, a large underserved market, or management that knows how to reinvest well. Weak growth may come from discounts, hype, heavy spending, or a temporary trend.\n\nImagine two companies both growing revenue at 30 percent. The first keeps customers for years, sells more to them over time, and becomes more profitable as it scales. The second keeps buying new customers through expensive ads, but people leave quickly and margins never improve. Same growth rate on the surface. Very different business quality underneath.\n\nThis is why the growth lens asks you to slow down. It is not enough to see a chart moving up. It is not enough to hear that an industry is the future. The real question is whether this specific business can turn growth into durable value.\n\nThe tradeoff is that this takes attention. Growth investing usually asks more of you than a simple broad market approach. You are not just buying and forgetting. You are watching whether the thesis remains alive. If the original reason for growth breaks, the story needs to be revisited.\n\nInside Nisk, the useful habit is to write the thesis before emotion takes over. What do you believe about the market? What do you believe about the company’s advantage? What would show that the thesis is working? What would show that it is breaking? This gives future you a way to think clearly instead of reacting to every headline.\n\nThe takeaway is that growth investing is not just speed worship. Fast growth can be impressive, but quality matters more. The best version of this lens studies whether growth can last, whether it can become profitable, and whether the business is actually getting stronger.',
			flashcards: [
				{
					id: 'school-growth-investing-04-fc-1',
					role: 'main_idea',
					front: 'What growth signal matters most?',
					back: 'The quality and durability of growth, not speed alone.',
				},
				{
					id: 'school-growth-investing-04-fc-2',
					role: 'watch_out',
					front: 'Why can revenue growth mislead?',
					back: 'It may come from heavy spending, weak retention, or a temporary trend.',
				},
				{
					id: 'school-growth-investing-04-fc-3',
					role: 'use_it',
					front: 'How do you track a thesis?',
					back: 'Write what would prove the story is working or breaking.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Write the thesis test',
				prompt: 'Write three signs that would support a growth thesis and three signs that would weaken it.',
			},
			revision: 1,
		},
		{
			id: 'school-growth-investing-05',
			title: 'Where growth investing breaks',
			description:
				'This lesson teaches how the growth lens can turn into hype, overconfidence, and story driven risk.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['growth-investing'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: ['growth-investing', 'hype-cycle', 'style-humility'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['story risk', 'hype cycle', 'style humility'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Growth investing breaks when the story becomes more important than the business. The future starts doing too much work.',
				},
				{
					type: 'paragraph',
					text: 'This school is useful because it notices upside, innovation, and long run change. But it can go wrong when investors treat possibility like certainty.',
				},
				{
					type: 'bullet_list',
					items: [
						'Excitement is not evidence',
						'Growth is not guaranteed',
						'Price still matters',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is mistaking a believable future for a likely future.',
				},
				{
					type: 'example',
					text: 'A new industry may truly matter, but many companies inside it may still fail, dilute owners, lose money, or disappoint expectations. Being right about the theme does not mean being right about the investment.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is overconfidence. Growth investors can underestimate competition, execution risk, valuation, interest rates, and how long adoption really takes.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens should end with humility. A worldview can help you see something real without becoming your whole identity.',
				},
				{
					type: 'key_takeaway',
					text: 'Growth investing is strongest when excitement is checked by evidence, price, and humility.',
				},
			],
			audioScript:
				'Every school of thought has a place where it breaks. Growth investing breaks when the story becomes stronger than the evidence.\n\nThe growth lens is useful because it notices things other lenses can miss. It sees innovation. It sees expanding markets. It sees companies that are not fully understood yet. It sees that today’s profits may be small because the business is reinvesting into a much larger future. That is valuable. Without this lens, it is easy to dismiss anything that looks expensive today.\n\nBut the same strength creates the danger. Growth investing can turn possibility into certainty. A company could become huge, so people start acting like it will. A technology could change an industry, so people start acting like every company in that industry will win. A founder sounds convincing, so people start treating confidence as proof. The future starts carrying too much weight.\n\nThis is where hype enters. Hype is not always fake. Sometimes the theme is real. The internet was real. Smartphones were real. Cloud software was real. Artificial intelligence may be real in powerful ways. But being right about a theme is not the same as being right about the specific investment, the timing, or the price.\n\nA new industry can matter and still destroy investor money along the way. Many companies can compete. Some can fail. Some can grow revenue but never earn enough profit. Some can issue more shares and dilute owners. Some can be great products but weak businesses. Some can be real businesses bought at prices that were too optimistic.\n\nThat is the part growth investors have to respect. Excitement is not evidence. Adoption is not guaranteed. A large market does not mean one company captures it. Fast growth does not mean high quality growth. And a beautiful future does not remove valuation risk.\n\nThe blind spot is overconfidence. Growth investors can underestimate competition, execution risk, changing interest rates, management mistakes, customer fatigue, and how hard it is for a business to grow into a massive valuation. The story can become so emotionally satisfying that negative information feels like noise.\n\nInside Nisk, the point is not to reject growth investing. The point is to use it cleanly. This worldview helps you ask better questions about the future, but it should not become your identity. You can respect the lens without becoming loyal to every exciting company or every big theme.\n\nA healthier growth mindset sounds like this: “This could become much larger, but here is what would have to happen. Here is what could break. Here is what the price already assumes. Here is how I would know I was wrong.”\n\nThat is mature growth investing. It combines imagination with discipline. It makes room for upside without pretending the future is promised.\n\nThe takeaway is simple. Growth investing is strongest when excitement is checked by evidence, price, and humility.',
			flashcards: [
				{
					id: 'school-growth-investing-05-fc-1',
					role: 'main_idea',
					front: 'Where does growth investing break?',
					back: 'When story, hype, and possibility become stronger than evidence.',
				},
				{
					id: 'school-growth-investing-05-fc-2',
					role: 'watch_out',
					front: 'What does theme risk mean?',
					back: 'A real trend can exist while many investments inside it still disappoint.',
				},
				{
					id: 'school-growth-investing-05-fc-3',
					role: 'use_it',
					front: 'How do you add humility?',
					back: 'Write what would prove the thesis wrong before emotions take over.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Name the break point',
				prompt: 'Write the clearest reason a growth story might fail, even if the company sounds exciting.',
			},
			revision: 1,
		},
	],
};
